import { useCallback, useEffect, useState } from 'react';
import {
  deleteFormField, listFormFields, upsertFormField, type FormFieldUpsert,
} from '../../api/campaigns';
import { listDinamicasDeCampania, type DinamicaRow } from '../../api/dinamicas';
import type { CampaignRow, FormFieldRow } from '../../lib/db-types';

type Kind = 'texto' | 'numero' | 'seleccion' | 'foto';
type Moment = 'ingreso' | 'salida' | 'ambos';

const KIND_LABEL: Record<Kind, string> = {
  texto: 'Texto', numero: 'Número', seleccion: 'Selección', foto: 'Foto',
};
const MOMENT_LABEL: Record<Moment, string> = {
  ingreso: 'Al marcar ingreso', salida: 'Al marcar salida', ambos: 'En ambos',
};

interface FieldForm {
  id: string | null;
  label: string;
  kind: Kind;
  optionsText: string;
  required: boolean;
  moment: Moment;
  sort: string;
  /** '' = se pide siempre; si no, el id de la dinámica que lo condiciona. */
  dynamicId: string;
}

const EMPTY_FIELD: FieldForm = {
  id: null, label: '', kind: 'texto', optionsText: '', required: false, moment: 'ingreso',
  sort: '0', dynamicId: '',
};

export default function CampaniaFormulario({ campaign, soloLectura = false }: {
  campaign: CampaignRow;
  /** El formulario del marcado es parte de la definición de la campaña: lo arma quien la
   *  programa. El coordinador y el supervisor lo consultan para saber qué se le pide al
   *  recurso en cada marcado. */
  soloLectura?: boolean;
}) {
  const [fields, setFields] = useState<FormFieldRow[]>([]);
  const [dinamicas, setDinamicas] = useState<DinamicaRow[]>([]);
  const [form, setForm] = useState<FieldForm | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setFields(await listFormFields(campaign.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron cargar los campos del formulario');
    }
  }, [campaign.id]);

  useEffect(() => { void load(); }, [load]);

  // Las dinámicas de la campaña, para poder atar un campo a una de ellas. Si falla no se corta la
  // pantalla: sin la lista, el selector queda en "Siempre" y el formulario se sigue editando.
  useEffect(() => {
    listDinamicasDeCampania(campaign.id).then(setDinamicas).catch(() => setDinamicas([]));
  }, [campaign.id]);

  function openNew() {
    setFormError(null);
    setForm({ ...EMPTY_FIELD, sort: String(fields.length) });
  }

  function openEdit(f: FormFieldRow) {
    setFormError(null);
    setForm({
      id: f.id,
      label: f.label,
      kind: f.kind as Kind,
      optionsText: (f.options ?? []).join(', '),
      required: f.required,
      moment: f.moment as Moment,
      sort: String(f.sort),
      dynamicId: f.dynamic_id ?? '',
    });
  }

  async function save() {
    if (!form) return;
    const label = form.label.trim();
    if (label === '') { setFormError('La etiqueta es obligatoria'); return; }
    const sort = Number(form.sort.trim() === '' ? '0' : form.sort);
    if (!Number.isInteger(sort) || sort < 0) {
      setFormError('El orden debe ser un número entero mayor o igual a 0'); return;
    }
    let options: string[] | null = null;
    if (form.kind === 'seleccion') {
      options = form.optionsText.split(',').map((o) => o.trim()).filter((o) => o !== '');
      if (options.length < 2) {
        setFormError('Un campo de selección necesita al menos 2 opciones separadas por coma'); return;
      }
    }
    const row: FormFieldUpsert = {
      ...(form.id ? { id: form.id } : {}),
      campaign_id: campaign.id,
      label,
      kind: form.kind,
      options,
      required: form.required,
      moment: form.moment,
      sort,
      dynamic_id: form.dynamicId === '' ? null : form.dynamicId,
    };
    try {
      await upsertFormField(row);
      setForm(null);
      await load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'No se pudo guardar el campo');
    }
  }

  async function remove(f: FormFieldRow) {
    if (!window.confirm(`¿Eliminar el campo "${f.label}"?`)) return;
    try {
      await deleteFormField(f.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo eliminar el campo');
    }
  }

  return (
    <section className="border rounded p-4 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="font-semibold">Formulario del marcado</h2>
        {!soloLectura && (
          <button className="px-3 py-2 bg-blue-600 text-white rounded" onClick={openNew}>
            Agregar campo
          </button>
        )}
      </div>
      <p className="text-sm text-gray-600 mb-3">
        Campos extra que el recurso llena al marcar en esta campaña, además de la foto y el GPS.
      </p>
      {error && <p className="text-red-600 mb-2">{error}</p>}

      <div className="overflow-x-auto">
        <table className="w-full text-sm border">
          <thead>
            <tr className="bg-gray-100 text-left">
              <th className="p-2">Orden</th><th className="p-2">Etiqueta</th><th className="p-2">Tipo</th>
              <th className="p-2">Opciones</th><th className="p-2">Obligatorio</th>
              <th className="p-2">Momento</th><th className="p-2">Cuándo aplica</th>
              {!soloLectura && <th className="p-2">Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {fields.length === 0 && (
              <tr><td className="p-2" colSpan={8}>Sin campos extra: el marcado pedirá solo foto y GPS</td></tr>
            )}
            {fields.map((f) => (
              <tr key={f.id} className="border-t">
                <td className="p-2">{f.sort}</td>
                <td className="p-2">{f.label}</td>
                <td className="p-2">{KIND_LABEL[f.kind as Kind] ?? f.kind}</td>
                <td className="p-2">{(f.options ?? []).join(', ')}</td>
                <td className="p-2">{f.required ? 'Sí' : 'No'}</td>
                <td className="p-2">{MOMENT_LABEL[f.moment as Moment] ?? f.moment}</td>
                <td className="p-2">
                  {f.dynamic_id === null
                    ? <span className="text-gray-600">Siempre</span>
                    : (
                      <span className="text-indigo-700">
                        Solo con «{dinamicas.find((d) => d.id === f.dynamic_id)?.name ?? 'dinámica'}»
                      </span>
                    )}
                </td>
                {!soloLectura && (
                  <td className="p-2 flex gap-2">
                    <button className="text-blue-600 underline" onClick={() => openEdit(f)}>Editar</button>
                    <button className="text-red-600 underline" onClick={() => void remove(f)}>Eliminar</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded p-4 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="font-semibold mb-3">{form.id ? 'Editar campo' : 'Nuevo campo'}</h3>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm col-span-2">Etiqueta
                <input className="border rounded px-2 py-1 w-full" value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })} />
              </label>
              <label className="text-sm">Tipo
                <select className="border rounded px-2 py-1 w-full" value={form.kind}
                  onChange={(e) => setForm({ ...form, kind: e.target.value as Kind })}>
                  <option value="texto">Texto</option>
                  <option value="numero">Número</option>
                  <option value="seleccion">Selección</option>
                  <option value="foto">Foto</option>
                </select>
              </label>
              <label className="text-sm">Momento
                <select className="border rounded px-2 py-1 w-full" value={form.moment}
                  onChange={(e) => setForm({ ...form, moment: e.target.value as Moment })}>
                  <option value="ingreso">Al marcar ingreso</option>
                  <option value="salida">Al marcar salida</option>
                  <option value="ambos">En ambos</option>
                </select>
              </label>
              {form.kind === 'seleccion' && (
                <label className="text-sm col-span-2">Opciones (separadas por coma)
                  <input className="border rounded px-2 py-1 w-full" value={form.optionsText}
                    placeholder="Sí, No, No aplica"
                    onChange={(e) => setForm({ ...form, optionsText: e.target.value })} />
                </label>
              )}
              {/*
                Atar el campo a una dinámica (U12). Era lo único del formulario que seguía
                dependiendo de SQL: la pregunta "¿Cuántos raspa y gana entregaste en el turno?" se
                sembró a mano el 14-ago justamente porque esto no existía.

                La consecuencia se dice acá y no en una ayuda: es la diferencia entre preguntarle
                algo a las 5 tiendas del fin de semana y preguntárselo a las 19 todos los días.
              */}
              <label className="text-sm col-span-2">
                ¿Cuándo se pide?
                <select
                  className="border rounded px-2 py-1 w-full"
                  value={form.dynamicId}
                  onChange={(e) => setForm({ ...form, dynamicId: e.target.value })}
                >
                  <option value="">Siempre, en toda la campaña</option>
                  {dinamicas.map((d) => (
                    <option key={d.id} value={d.id}>Solo donde corra «{d.name}»</option>
                  ))}
                </select>
                <span className="text-xs text-gray-500">
                  {form.dynamicId === ''
                    ? 'Se le pide a todo el mundo en esta campaña, todos los días.'
                    : 'Solo aparece en las tiendas y los días en que esa dinámica está vigente. '
                      + 'Las vigencias se configuran en la pestaña Dinámicas.'}
                </span>
                {dinamicas.length === 0 && (
                  <span className="block text-xs text-amber-700">
                    Esta campaña no tiene dinámicas todavía. Créalas en la pestaña Dinámicas si
                    quieres condicionar un campo.
                  </span>
                )}
              </label>
              <label className="text-sm">Orden
                <input inputMode="numeric" className="border rounded px-2 py-1 w-full" value={form.sort}
                  onChange={(e) => setForm({ ...form, sort: e.target.value })} />
              </label>
              <label className="text-sm flex items-center gap-2 mt-5">
                <input type="checkbox" checked={form.required}
                  onChange={(e) => setForm({ ...form, required: e.target.checked })} />
                Obligatorio
              </label>
            </div>
            {formError && <p className="text-red-600 mt-2">{formError}</p>}
            <div className="flex gap-2 mt-4 justify-end">
              <button className="px-3 py-2 bg-gray-200 rounded" onClick={() => setForm(null)}>Cancelar</button>
              <button className="px-3 py-2 bg-blue-600 text-white rounded" onClick={() => void save()}>Guardar</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
